import React, { useCallback, useEffect, useMemo } from 'react';

import { FormComponentProps } from 'antd/lib/form';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import type { RosterParent } from '@appTypes/esb';
import { useUnitRoster } from '@modules/advancement/duck/hooks';
import type { Position } from '@modules/advancement/duck/types';
import { subUnitTypes } from '@modules/advancement/subUnits';
import { editSubUnitRequest } from '@modules/advancement/subUnits/duck/actions';
import {
  approvedSubUnitsSel,
  createSubUnitInProgressSel,
  subUnitTypeSel,
} from '@modules/advancement/subUnits/duck/selectors';
import {
  FormattedSubUnitDenChief,
  FormattedSubUnitYouth,
  SubUnitDenChiefPosition,
  mapSubUnitAdults,
  mapSubUnitDenChiefs,
  mapSubUnitScouts,
} from '@modules/advancement/utilsTyped';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { setShouldMemberDetailsReload } from '@modules/youthProfile/duck/actions';
import { hasNewPermissionSel } from '@shared';
import { Button, Col, Form, Row, Select } from '@shared/components';
import { PositionId } from '@shared/constants';
import { intl } from '@shared/localization';
import { newPermissions } from '@shared/newPermissions';
import { validateForm } from '@utils';

import {
  currentUserSubunitsSel,
  setSubUnitUserData,
  subUnitUserDataSel,
} from '../../duck';
import styles from './AddToSubUnitForm.less';

const { Item: FormItem } = Form;

type Props = FormComponentProps;

const AddToSubUnitForm: React.FC<Props> = ({ form }) => {
  const dispatch = useDispatch();
  const subUnitType = useSelector(subUnitTypeSel);
  const isLoadingCreateSubUnit = useSelector(createSubUnitInProgressSel);
  const subUnitsList = useSelector(approvedSubUnitsSel);
  const userData = useSelector(subUnitUserDataSel);
  const canEditAllSubUnits = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_UNIT),
  );
  const canEditMySubUnits = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_SUB_UNIT),
  );
  const mySubUnits: Position[] = useSelector(currentUserSubunitsSel);
  const { parents } = useUnitRoster();
  const { getFieldDecorator } = form;
  const isDen = subUnitType === subUnitTypes.DEN;
  const hasSubUnitOnlyPerms = useMemo(
    () => canEditMySubUnits,
    [canEditMySubUnits],
  );

  const filteredSubUnitList = useMemo(() => {
    if (hasSubUnitOnlyPerms && !canEditAllSubUnits) {
      return subUnitsList.filter(sub =>
        mySubUnits.find(mySub => {
          const mySubId = mySub.denId || mySub.patrolId;
          return mySubId === sub.subUnitId;
        }),
      );
    }

    return subUnitsList;
  }, [mySubUnits, hasSubUnitOnlyPerms, subUnitsList, canEditAllSubUnits]);

  useEffect(
    () => () => {
      dispatch(setSubUnitUserData({ subUnitId: 0, subUnitName: '' }));
    },

    [dispatch],
  );

  const validate = useCallback(() => validateForm(form), [form]);

  const handleSaveSubUnit = useCallback(async () => {
    const subUnitData = await validate();
    if (subUnitData) {
      dispatch(setShouldMemberDetailsReload(true));
      const isAdult = userData.isAdult;
      const isDenChief = userData.positions?.find(
        (pos: SubUnitDenChiefPosition) =>
          !pos.denId && !pos.patrolId && pos.positionId === PositionId.DenChief,
      );

      const selectedSubUnit = subUnitsList.find(
        subUnit => subUnit.subUnitId === Number(subUnitData.subUnitId),
      );

      dispatch(
        editSubUnitRequest({
          subUnitName: selectedSubUnit?.subUnitNameRaw as string,
          selectedUsers:
            isAdult || isDenChief
              ? []
              : [
                  mapSubUnitScouts(
                    userData,
                    isDen,
                    selectedSubUnit?.denType,
                    parents as RosterParent[],
                  ) as FormattedSubUnitYouth,
                ],
          selectedAdults:
            isAdult && !isDenChief ? [mapSubUnitAdults(userData, isDen)] : [],
          selectedDenChiefs:
            isDenChief && featureFlags.getFlag('SBL_5140_DENCHIEF')
              ? [mapSubUnitDenChiefs(userData) as FormattedSubUnitDenChief]
              : [],
          subUnitId: Number(subUnitData.subUnitId),
          denType:
            isDen && selectedSubUnit?.denType
              ? selectedSubUnit.denType
              : undefined,
        }),
      );
    }
  }, [validate, isDen, userData, subUnitsList, parents, dispatch]);

  return (
    <Row type="flex" justify="space-between">
      <Col span={24}>
        <FormattedHTMLMessage
          id={'packRoster.AddSubUnit.AddToSubUnitForm.Instruction'}
          values={{ personFullName: userData.personFullName }}
        />
      </Col>
      <Col xs={24} lg={11}>
        <FormItem>
          {getFieldDecorator('subUnitId', {
            initialValue: filteredSubUnitList[0]?.subUnitId,
            rules: [
              {
                required: true,
                message: intl.formatMessage({
                  id: 'shared.form.error.isRequired',
                }),
              },
            ],
          })(
            <Select size="large">
              <Select.OptGroup>
                {filteredSubUnitList.map(({ subUnitId, subUnitName }) => (
                  <Select.Option key={subUnitId} value={subUnitId}>
                    {subUnitName}
                  </Select.Option>
                ))}
              </Select.OptGroup>
            </Select>,
          )}
        </FormItem>
      </Col>
      <Col span={24} className={styles.saveButton}>
        <Button onClick={handleSaveSubUnit} disabled={isLoadingCreateSubUnit}>
          <FormattedMessage id="shared.save" />
        </Button>
      </Col>
    </Row>
  );
};

export default Form.create()(AddToSubUnitForm);
